import { getJson, getList, send } from './client';
import type {
  AudienceMembersResult,
  AudienceMeta,
  AudiencePreview,
  CallLogResult,
  Campaign,
  CampaignAudience,
  CampaignChannel,
  CampaignAudienceFilters,
  CampaignLinkTracking,
  CampaignRecipient,
  CampaignRecipientFilter,
  CampaignReview,
  CampaignResendSegment,
  CampaignSummaryPayload,
  CampaignSendMode,
  CampaignWebsite,
  CampaignActivity,
  CampaignBookings,
  CampaignBookingCount,
  CampaignHomeOverview,
  CampaignListFilters,
  CampaignMonthBookings,
  CampaignPreflight,
  CampaignAutomation,
  AutomationPreview,
  ShortLinkDomainState,
  CampaignSettings,
  CannedResponse,
  ComposeDraft,
  ComposeDraftSummary,
  ContactBookings,
  DispatchAudience,
  DispatchBooking,
  Conversation,
  ConversationBooking,
  ConversationDraft,
  ConversationListResult,
  ConversationStatus,
  CrmContact,
  Label,
  LatestContactBookings,
  SubjectBookings,
  MergeField,
  Message,
  NotificationListResult,
  NotificationMute,
  NotificationPreferences,
  TemplateBlock,
  SmsPreview,
  Suppression,
} from './types';

/**
 * Communications endpoints (docs/api.md). Conversations are addressed by their
 * DISPLAY id everywhere; message ids are global and double as paging cursors.
 */

// Shared catalogs the module needs behind its data-source seam.
export { listAgents, listInboxes } from './endpoints';
// The softphone's credential. Behind the seam like everything else, so the
// preview route is forced to stub it rather than reaching for a real token.
export {
  addCallParticipant,
  getVoiceToken,
  listCallParticipants,
  removeCallParticipant,
  setCallRecording,
  transferCall,
} from './endpoints';

// ---- Conversations ----------------------------------------------------------

export interface ConversationFilters {
  /** One status, several ('open' + 'pending' for a followed assistant), or all. */
  status?: ConversationStatus | ConversationStatus[] | 'all';
  inboxId?: number;
  assigneeType?: 'me' | 'unassigned' | 'all';
  /** Only conversations the assistant has answered — sent or drafted. */
  botHandled?: boolean;
  /** The Sent view: only conversations the team has replied to at least once. */
  sent?: boolean;
  /** The Starred view: only conversations somebody flagged, across every status. */
  starred?: boolean;
  /** The Drafts view: conversations where THIS agent left a reply unsent. */
  hasDraft?: boolean;
  labels?: string[];
  q?: string;
  page?: number;
  /** `draft_updated` is honoured only alongside `hasDraft` — see the server. */
  sortBy?: 'latest' | 'created_at' | 'waiting_since' | 'draft_updated' | 'last_outgoing';
  updatedWithin?: number;
}

export const listConversations = (filters: ConversationFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) {
    params.set('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
  }
  if (filters.inboxId !== undefined) params.set('inbox_id', String(filters.inboxId));
  if (filters.assigneeType) params.set('assignee_type', filters.assigneeType);
  if (filters.botHandled) params.set('bot_handled', '1');
  if (filters.sent) params.set('sent', '1');
  if (filters.starred) params.set('starred', '1');
  if (filters.hasDraft) params.set('has_draft', '1');
  if (filters.labels?.length) params.set('labels', filters.labels.join(','));
  if (filters.q) params.set('q', filters.q);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.sortBy) params.set('sort_by', filters.sortBy);
  if (filters.updatedWithin) params.set('updated_within', String(filters.updatedWithin));
  const query = params.toString();
  return getJson<ConversationListResult>(`/conversations${query ? `?${query}` : ''}`);
};

export const getConversation = (displayId: number) =>
  getJson<Conversation>(`/conversations/${displayId}`);

/**
 * The agent's own unsent reply on this conversation.
 *
 * Its own read rather than a field on the conversation because the LIST carries
 * only a one-line summary of it: 25 rows of somebody's half-written mail is a
 * payload nobody reads. The composer asks for the body when it is about to put
 * it back in the box.
 */
export const getDraft = (displayId: number) =>
  getJson<{ draft: ConversationDraft | null }>(`/conversations/${displayId}/draft`);

/**
 * Save what is in the composer. An empty one DELETES the draft server-side, so
 * a sent message needs no separate clear — the box empties and saves itself.
 * PUT, not POST: one row per person per conversation, and a save retried after
 * a dropped connection must not be able to leave two.
 */
export const saveDraft = (
  displayId: number,
  body: {
    content: string;
    bodyHtml?: string | null;
    isNote: boolean;
    attachments: OutgoingAttachment[];
    /** Email only. A malformed address here is DROPPED rather than refused —
     *  this is called on a debounce while somebody is still typing one. */
    toEmails?: string[];
    ccEmails?: string[];
    bccEmails?: string[];
    /** Set only on a forward; a reply is titled by the thread. */
    subject?: string | null;
  },
) => send<{ draft: ConversationDraft | null }>('PUT', `/conversations/${displayId}/draft`, body);

export const deleteDraft = (displayId: number) =>
  send<{ draft: null }>('DELETE', `/conversations/${displayId}/draft`);

// ---------------------------------------------------------------------------
// Compose drafts: a new message begun to somebody with no thread yet.
// ---------------------------------------------------------------------------

/** What the compose panel sends on every save — the whole box, never a patch. */
export interface ComposeDraftBody {
  /** The sending account. Re-sent on every save because the agent can switch it. */
  inboxId: number;
  /** The recipient, one of two ways. `contactId` wins when both are given. */
  contactId?: number | null;
  toAddress?: string | null;
  toIsEmail?: boolean;
  subject?: string | null;
  content: string;
  bodyHtml?: string | null;
  attachments: OutgoingAttachment[];
  /**
   * The email header. `toEmails` is the EXTRA To lines only — the picked
   * recipient rides in `contactId`/`toAddress` and must not be repeated here, or
   * a stale copy of their address would outrank their contact record on send.
   *
   * A half-typed address is dropped server-side rather than refused: this is
   * called on a debounce while somebody is still typing one.
   */
  toEmails?: string[];
  ccEmails?: string[];
  bccEmails?: string[];
}

/**
 * This agent's unsent new messages, newest edit first.
 *
 * Summaries, not bodies: the panel asks for the full draft when it is about to
 * put one back in the box, exactly as the reply composer does. Narrowed
 * server-side to channels the agent can still send on — a draft on a channel
 * their access has since lost is one they could not finish.
 */
export const listComposeDrafts = () =>
  getJson<{ payload: ComposeDraftSummary[] }>('/compose_drafts').then((r) => r.payload);

export const getComposeDraft = (id: number) =>
  getJson<{ draft: ComposeDraft }>(`/compose_drafts/${id}`).then((r) => r.draft);

/**
 * Start one. Answers `draft: null` for a box with nothing in it — a blank
 * composer is not a draft to keep, and an id for one would give the panel a row
 * to patch that should never have existed.
 */
export const createComposeDraft = (body: ComposeDraftBody) =>
  send<{ draft: ComposeDraft | null }>('POST', '/compose_drafts', body).then((r) => r.draft);

/**
 * Replace one. PUT because the panel always sends the whole box; emptying it
 * DELETES the draft server-side, which is why the answer is nullable — the same
 * lifecycle the reply composer's save has.
 */
export const saveComposeDraft = (id: number, body: ComposeDraftBody) =>
  send<{ draft: ComposeDraft | null }>('PUT', `/compose_drafts/${id}`, body).then((r) => r.draft);

/** Discard, and what a successful send calls. Answers the same if it was gone. */
export const deleteComposeDraft = (id: number) =>
  send<{ draft: null }>('DELETE', `/compose_drafts/${id}`);

export const createConversation = (body: {
  inboxId: number;
  contactId: number;
  /** The first message. Same body shape as a reply — see sendMessage. */
  message?: {
    content: string;
    /** Email inboxes only; sets the new thread's subject (mailSubject). */
    subject?: string;
    /** Styled body; the server sanitizes it and mails it as the html part. */
    bodyHtml?: string;
    contentAttributes?: Record<string, unknown>;
    attachments?: OutgoingAttachment[];
  };
}) => send<Conversation>('POST', '/conversations', body);

export const toggleStatus = (
  displayId: number,
  status: ConversationStatus,
  snoozedUntil?: string,
) =>
  send<{ id: number; status: ConversationStatus; snoozedUntil: string | null }>(
    'POST',
    `/conversations/${displayId}/toggle_status`,
    snoozedUntil ? { status, snoozedUntil } : { status },
  );

/**
 * Flag a thread for the team. The wanted state travels rather than a flip, so
 * two agents pressing it at once agree instead of undoing each other.
 */
export const toggleStar = (displayId: number, starred: boolean) =>
  send<{ id: number; starred: boolean }>('POST', `/conversations/${displayId}/toggle_star`, {
    starred,
  });

/**
 * Take a conversation out of the inbox. Internal to us like the star: nothing
 * is deleted at the channel and no provider is told. The server soft-deletes,
 * so a thread cleared in error is recoverable — but not from this console.
 */
export const deleteConversation = (displayId: number) =>
  send<{ id: number; deleted: true }>('DELETE', `/conversations/${displayId}`);

export const assignConversation = (displayId: number, assigneeDashBoardId: number | null) =>
  send<{ id: number; assigneeDashBoardId: number | null }>(
    'POST',
    `/conversations/${displayId}/assignments`,
    { assigneeDashBoardId },
  );

/**
 * Put the assistant back in charge — the inverse of the silent takeover a
 * public reply performs. `engaged` says whether it was set going on a message
 * the customer is already waiting on, or is simply holding the thread until
 * they write again. Refused with a named reason when no sending assistant
 * answers the channel, or it already has the conversation.
 */
export const handBackToBot = (displayId: number) =>
  send<{
    id: number;
    status: ConversationStatus;
    assigneeIsBot: boolean;
    assigneeDashBoardId: number | null;
    engaged: boolean;
  }>('POST', `/conversations/${displayId}/hand_back_to_bot`);

export const setConversationLabels = (displayId: number, labels: string[]) =>
  send<{ id: number; labels: string[] }>('POST', `/conversations/${displayId}/labels`, {
    labels,
  });

/**
 * Put the badge back. Unread is a SHARED column, so this un-reads the thread for
 * the whole team — the same symmetry as opening it reading it for everybody.
 */
export const markConversationUnread = (displayId: number) =>
  send<{ id: number; agentLastSeenAt: null }>('POST', `/conversations/${displayId}/mark_unread`);

export const markConversationSeen = (displayId: number) =>
  send<{ id: number; agentLastSeenAt: string }>(
    'POST',
    `/conversations/${displayId}/update_last_seen`,
  );

/**
 * The same stamp across a whole list. `inboxIds` empty means every channel this
 * agent can read; the server bounds one call, so `more` means run it again.
 */
export const markAllConversationsSeen = (inboxIds: number[] = []) =>
  send<{ count: number; more: boolean }>('POST', '/conversations/mark_all_seen', { inboxIds });

// ---- Messages ---------------------------------------------------------------

export const listMessages = (
  displayId: number,
  cursor?: { before?: number; after?: number; limit?: number },
) => {
  const params = new URLSearchParams();
  if (cursor?.before) params.set('before', String(cursor.before));
  if (cursor?.after) params.set('after', String(cursor.after));
  // Backward pages only, and the server clamps it — see controller/messageCont.js.
  if (cursor?.limit) params.set('limit', String(cursor.limit));
  const query = params.toString();
  return getList<Message>(`/conversations/${displayId}/messages${query ? `?${query}` : ''}`);
};

export interface OutgoingAttachment {
  s3Key: string;
  fileName: string;
  fileType: 'image' | 'audio' | 'video' | 'file';
  fileSize: number;
  extension: string;
}

export const sendMessage = (
  displayId: number,
  body: {
    content: string;
    /** Rich-text reply body. The server sanitizes it, renders it in the thread and
     *  mails it as the html part; `content` stays the plain-text fallback. */
    bodyHtml?: string;
    private?: boolean;
    echoId?: string;
    contentAttributes?: Record<string, unknown>;
    attachments?: OutgoingAttachment[];
  },
) => send<Message>('POST', `/conversations/${displayId}/messages`, body);

export const retryMessage = (displayId: number, messageId: number) =>
  send<{ id: number; status: string }>(
    'POST',
    `/conversations/${displayId}/messages/${messageId}/retry`,
  );

/**
 * Delete one message, internally. The row stays and the thread keeps its shape:
 * the server replaces the body with a placeholder and marks it deleted, which
 * is what the bubble renders. Nothing is unsent — a message already delivered
 * is on the customer's phone, and this only decides what the team reads back.
 */
export const deleteMessage = (displayId: number, messageId: number) =>
  send<Message>('DELETE', `/conversations/${displayId}/messages/${messageId}`);

// ---- Contacts ---------------------------------------------------------------

export const listContacts = (q: string, page = 1) => {
  const params = new URLSearchParams({ page: String(page) });
  if (q) params.set('q', q);
  return getList<CrmContact>(`/contacts?${params}`);
};

export const getContact = (id: number) => getJson<CrmContact>(`/contacts/${id}`);

export const createContact = (body: {
  name?: string;
  email?: string;
  phoneNumber?: string;
}) => send<CrmContact>('POST', '/contacts', body);

export const listContactConversations = (id: number) =>
  getList<Conversation>(`/contacts/${id}/conversations`);

/** The linked dispatch customer's rides and quotes. Never cached server-side. */
export const listContactBookings = (id: number) =>
  getJson<ContactBookings>(`/contacts/${id}/bookings`);

/**
 * Each of these contacts' last reservation (the one most recently made), in ONE
 * dispatch read — what the mail list draws beside a sender. Asked per page of
 * rows, never per row; the service drops ids past 50.
 */
export const listLatestContactBookings = (contactIds: readonly number[]) =>
  getJson<LatestContactBookings>(`/contacts/latest_bookings?ids=${contactIds.join(',')}`);

/**
 * The reservation each of these mail threads' subjects names, by display id —
 * one dispatch read per page of rows, as above. Threads whose subject names
 * nothing dispatch has are left out; the service drops ids past 50.
 */
export const listSubjectBookings = (conversationIds: readonly number[]) =>
  getJson<SubjectBookings>(`/conversations/subject_bookings?ids=${conversationIds.join(',')}`);

/**
 * Who this contact may be in dispatch — suggestions with no `q`, a search with
 * one. Nothing is written; linking is the two calls below.
 */
export const getBookerSuggestions = (contactId: number, q?: string) => {
  const params = new URLSearchParams();
  if (q?.trim()) params.set('q', q.trim());
  const query = params.toString();
  return getJson<BookerSuggestions>(`/contacts/${contactId}/booker_suggestions${query ? `?${query}` : ''}`);
};

/**
 * An agent saying which dispatch customer this address is — or, with `null`,
 * that it is nobody (which the auto-link then leaves alone). Holds for every
 * later message from the address. `clearAffiliate` forgets a remembered desk
 * in the same write, for a pick made in the booking box.
 */
export const linkDispatchCustomer = (
  contactId: number,
  dispatchCustomerId: number | null,
  opts: { clearAffiliate?: boolean } = {},
) =>
  send<CrmContact>('POST', `/contacts/${contactId}/link_dispatch_customer`, {
    dispatchCustomerId,
    ...(opts.clearAffiliate ? { clearAffiliate: true } : {}),
  });

/** Remember (or with `null`, forget) the affiliate this address books for. */
export const linkDispatchAffiliate = (contactId: number, dispatchAffiliateId: number | null) =>
  send<CrmContact>('POST', `/contacts/${contactId}/link_dispatch_affiliate`, { dispatchAffiliateId });

/** RingOut click-to-call: rings the agent first, then the contact. */
export const ringoutCall = (contactId: number, body: { inboxId: number; agentPhone?: string }) =>
  send<{ ringoutId: string; status: Record<string, string> }>(
    'POST',
    `/contacts/${contactId}/call`,
    body,
  );

/**
 * The same RingOut to a bare number, for the places with no contact to call —
 * a call-log row, and the dialer.
 */
export const ringoutNumber = (body: { inboxId: number; to: string; agentPhone?: string }) =>
  send<{ ringoutId: string; status: Record<string, string> }>('POST', '/calls/ringout', body);

// ---- Labels / canned responses / AI ----------------------------------------

export const listLabels = () => getList<Label>('/labels');

export const listCanned = () => getList<CannedResponse>('/canned_responses');

/**
 * The tenant's reusable email blocks, rendered. Empty when dispatch has not
 * granted the CRM its component table — the pickers hide themselves rather than
 * showing an error, because a tenant that never opened Template Studio has
 * nothing to show either and the two look the same to an agent.
 */
export const listTemplateBlocks = () => getList<TemplateBlock>('/template_blocks');

export const draftReply = (conversationDisplayId: number, instruction?: string) =>
  send<{ draft: string }>('POST', '/ai/draft_reply', {
    conversationDisplayId,
    ...(instruction ? { instruction } : {}),
  });

export type BookingServiceType = 'oneway' | 'hourly';

/**
 * The AI's booking extraction — free text only. Addresses and the vehicle wish
 * stay unresolved strings; the booking form resolves them against dispatch-be
 * (places proxy, vehicle-type list) before anything is submitted.
 */
export interface AiBookingDraft {
  serviceType?: BookingServiceType;
  durationHours?: number;
  pickupAddressText?: string;
  dropoffAddressText?: string;
  stops?: string[];
  /** Local wall time `YYYY-MM-DDTHH:mm`, no timezone suffix. */
  pickupDatetime?: string;
  passengerFirstName?: string;
  passengerLastName?: string;
  passengerPhone?: string;
  passengerEmail?: string;
  passengerCount?: number;
  luggageCount?: number;
  vehiclePreference?: string;
  flightNumber?: string;
  airline?: string;
  customerNote?: string;
  chauffeurNote?: string;
  /** The SENDER's own reservation number — dispatch's `requesterResNo`. */
  bookerReference?: string;
  /**
   * OUR own job or confirmation number, when the thread quotes one back at us
   * — a reply to a confirmation we sent, or a "Supplier Ref" on their sheet.
   * It is not a form field: nothing books it, and the form carries it only so
   * the duplicate check can be re-run when the agent edits a reference.
   */
  ourReference?: string;
  isQuoteSuggested?: boolean;
  returnTripRequested?: boolean;
  summary?: string;
}

/**
 * Who the mail came FROM, resolved against dispatch by the service — the booker,
 * which on a farm-in or a travel desk's forward is not the passenger.
 *
 * Both sides are sent, not just the winner: one mailbox can belong to an
 * affiliate contact AND a customer, and the agent may mean either. `suggested`
 * is what the form opens on. Absent (`null`) means dispatch knows this address
 * as neither, which is a guest booking — exactly the old behaviour.
 */
export interface BookingBookerAffiliate {
  id: number;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  primaryContactName: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  /** Which of the affiliate's addresses matched — shown, never asserted. */
  matchedEmail: string | null;
}

export interface BookingBookerCustomer {
  id: number;
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface BookingBooker {
  suggested: 'affiliate' | 'customer';
  matchedEmail: string | null;
  affiliate: BookingBookerAffiliate | null;
  customer: BookingBookerCustomer | null;
  /**
   * The suggested side is something an agent decided for this address (a
   * remembered desk, a customer linked by hand) rather than what it matched.
   * Absent on a service that predates it.
   */
  remembered?: boolean;
}

/** Why a candidate was offered — the picker says it rather than asserting a match. */
export type BookerMatchReason = 'name' | 'domain' | 'linked' | 'email' | 'phone';

/** A customer the search or the suggestions offered: the booker shape plus what tells two apart. */
export interface BookerCandidateCustomer extends BookingBookerCustomer {
  alternateEmail: string | null;
  customerType: { code: string; label: string } | null;
  /** The business account this person belongs to, when they belong to one. */
  businessName: string | null;
  matchedOn: BookerMatchReason[];
}

export interface BookerCandidateAffiliate extends BookingBookerAffiliate {
  matchedOn: BookerMatchReason[];
}

/**
 * Who a contact may be in dispatch. Without `q`: suggestions — the same email
 * domain (never a free-mail one), accounts the CRM already linked on that
 * domain, and the same name. With `q`: a search.
 *
 * `unavailable` is per side and is NEVER an empty list in disguise: a reason
 * there means dispatch could not be asked, which the picker renders as
 * "couldn't check" — an empty list with no reason means nobody matched.
 */
export interface BookerSuggestions {
  customers: BookerCandidateCustomer[];
  affiliates: BookerCandidateAffiliate[];
  unavailable: { customers: string | null; affiliates: string | null };
  /** The domain suggestions were drawn from; null for free mail or no email. */
  domain: string | null;
  freeMail: boolean;
  query: string | null;
}

/**
 * The tenant's two form-filling switches, from dispatch's System Configs, plus
 * what the second one needs to act on.
 *
 * Both are the dashboard's own rows (`admin-auto-fill-pax-info-new-job` and
 * `admin-prefill-pax-lug-from-veh`), read service-side so the defaults live in
 * one place — and both default ON, because the dashboard filled these fields
 * long before there were rows to consult. An older service sends no `prefill`
 * at all, which has to read the same way: fill, as this box always has.
 */
export interface BookingPrefill {
  /** May the passenger's phone and email be filled from whoever we know? */
  paxInfo: boolean;
  /** May Pax and Lug be filled from the chosen vehicle's capacity? */
  paxLugFromVehicle: boolean;
  /**
   * Capacity per vehicle type id, merged onto the vehicle list the box fetches
   * from dispatch — whose own endpoint carries ids and names only. Empty when
   * the switch is off, or when dispatch cannot tell us.
   */
  vehicleCapacities: BookingVehicleCapacity[];
}

export interface BookingVehicleCapacity {
  id: number;
  passengerCapacity: number | null;
  luggageCapacity: number | null;
}

export interface BookingClarification {
  question: string;
  answer: string;
}

export type BookingQuestionKind = 'text' | 'choice' | 'date' | 'time' | 'datetime' | 'number';

/**
 * One typed clarifying question. `kind` picks the control the agent answers
 * with (chips for `choice`, a picker for date/time); `options` only accompanies
 * `choice`. The service normalizes, but the box still treats both as untrusted
 * — an older service omits `questionItems` entirely and sends bare strings.
 */
export interface BookingQuestionItem {
  text: string;
  kind?: BookingQuestionKind;
  options?: string[];
}

/**
 * What to do with the thread's attachments. `auto` reads them, but stops and
 * asks when they are big; `skip` and `include` are the agent's two answers to
 * that question, and the box repeats its answer on every later round so the
 * same pile is never queried twice.
 */
export type BookingAttachmentMode = 'auto' | 'skip' | 'include';

/** One attachment the service offered to read, as the size prompt lists it. */
export interface BookingAttachmentFile {
  name: string;
  /** Bytes. */
  size: number;
}

/**
 * One booking already on file that carries a reference this request quotes.
 *
 * Two shapes in one, because the two sources answer different halves. `job` is
 * dispatch's own row — current, authoritative, and the only source that knows
 * about bookings made in the dashboard. `booking` is the CRM's record of making
 * one, which is the ONLY source for drafts (dispatch parks those where the CRM
 * cannot read them) and the only one that knows which conversation it came out
 * of. A job this console booked carries both.
 */
export interface BookingDuplicate {
  source: 'dispatch' | 'crm';
  kind: 'draft' | 'job' | 'quote';
  dispatchId: string;
  job: DispatchBooking | null;
  booking: ConversationBooking | null;
  /** WHICH reference matched — "your ref" and "job #" are different sentences. */
  matchedOn: { uid: boolean; requesterResNo: boolean };
}

export interface BookingDuplicateResult {
  /**
   * False means the check could NOT be completed — an ungranted column, an
   * unreachable dispatch. It must never be rendered as "no duplicates": a
   * silent all-clear is the exact failure this check exists to prevent.
   */
  checked: boolean;
  reason: string | null;
  references: { ourReference: string | null; bookerReference: string | null };
  matches: BookingDuplicate[];
}

export type DraftBookingResult =
  | {
      status: 'ready';
      draft: AiBookingDraft;
      booker?: BookingBooker | null;
      /** Present only when `booker` is null: the likely accounts, for the agent to pick. */
      bookerSuggestions?: BookerSuggestions | null;
      prefill?: BookingPrefill | null;
      /** Absent on a service that predates the check — not "none found". */
      duplicates?: BookingDuplicateResult;
    }
  | {
      status: 'needs_info';
      questions: string[];
      /** Same questions, typed — present on services that emit kinds/options. */
      questionItems?: BookingQuestionItem[];
      partial?: AiBookingDraft;
      booker?: BookingBooker | null;
      bookerSuggestions?: BookerSuggestions | null;
      prefill?: BookingPrefill | null;
      duplicates?: BookingDuplicateResult;
    }
  | {
      /**
       * Nothing was generated and nothing was spent: the attachments are over
       * the read-without-asking limit. Re-send with `attachments: 'skip'` or
       * `'include'` to continue.
       */
      status: 'attachments_too_large';
      attachments: {
        files: BookingAttachmentFile[];
        totalBytes: number;
        /** The threshold that was crossed, for wording the prompt. */
        limitBytes: number;
      };
    };

/**
 * Extraction only — nothing is written anywhere. `messageId` scopes the read
 * history to messages up to that id; `clarifications` are the agent's answers
 * to a previous needs_info round; `attachments` carries their answer to the
 * size prompt (omit it for the first attempt).
 */
export const draftBooking = (body: {
  conversationDisplayId: number;
  messageId?: number;
  clarifications?: BookingClarification[];
  attachments?: BookingAttachmentMode;
}) => send<DraftBookingResult>('POST', '/ai/draft_booking', body);

/** Every booking this conversation has produced in dispatch, newest first. */
export const listConversationBookings = (displayId: number) =>
  getList<ConversationBooking>(`/conversations/${displayId}/bookings`);

/**
 * Record what dispatch just created — the row the thread's strip reads, and the
 * private note beside it, written in that order by the service.
 *
 * Called AFTER the booking exists, so a failure here is never a reason to
 * create it again: the caller shows the error and leaves the booking alone.
 */
export const recordConversationBooking = (
  displayId: number,
  body: {
    kind: 'draft' | 'job' | 'quote';
    dispatchId: string;
    dispatchUid?: string | null;
    bookerReference?: string | null;
    summary?: string | null;
    pickupAt?: string | null;
    /** The message the draft was read from, when the run was scoped to one. */
    messageId?: number | null;
    trip?: ConversationBooking['trip'];
  },
) =>
  send<{ booking: ConversationBooking; notePosted: boolean }>(
    'POST',
    `/conversations/${displayId}/bookings`,
    body,
  );

/**
 * Has this trip been booked already? Re-run whenever the agent edits a
 * reference by hand — the draft-time answer cannot cover a number they typed.
 */
export const checkBookingDuplicates = (
  displayId: number,
  refs: { ourReference?: string | null; bookerReference?: string | null },
) => {
  const query = new URLSearchParams();
  if (refs.ourReference) query.set('ourReference', refs.ourReference);
  if (refs.bookerReference) query.set('bookerReference', refs.bookerReference);
  return getJson<BookingDuplicateResult>(
    `/conversations/${displayId}/booking_duplicates?${query.toString()}`,
  );
};

/**
 * Whether an AI call would work for this tenant at all — a key resolves, their
 * own or the platform's. The same capability probe the AI settings screen reads
 * (`GET /ai/models`, open to anyone signed in), asked BEFORE a prompt is
 * written, so a builder with no key says so and points at AI settings rather
 * than failing on the first Generate.
 */
export const getAiAvailability = () => getJson<{ available: boolean }>('/ai/models');

/**
 * Campaign copy assistant. 'generate' writes subject + body from `prompt`;
 * 'improve' polishes the current draft, steered by `instruction` when given.
 * Returns a draft for the builder to place — never touches a stored campaign.
 *
 * Sends no `channel`, which the server reads as email — the shape every console
 * that predates the text channel asks for.
 */
export const campaignCopy = (body: {
  mode: 'generate' | 'improve';
  prompt?: string;
  instruction?: string;
  subject?: string;
  bodyHtml?: string;
  audienceType?: CampaignAudience;
  fromName?: string;
}) => send<{ subject: string; bodyHtml: string }>('POST', '/ai/campaign_copy', body);

/**
 * The same assistant for a text campaign — one endpoint, told which channel it
 * is writing for, and a separate function here because what comes BACK is a
 * different shape. A union return would make every call site narrow a thing it
 * already knows, and an optional-everything one would let an email draft be
 * placed in a text box by a typo.
 *
 * The prompt behind it is written for this channel rather than shortened for it:
 * the segment budget is the craft, so it is what the model is told about.
 */
export const campaignSmsCopy = (body: {
  mode: 'generate' | 'improve';
  prompt?: string;
  instruction?: string;
  bodySms?: string;
  audienceType?: CampaignAudience;
  fromName?: string;
}) => send<{ bodySms: string }>('POST', '/ai/campaign_copy', { ...body, channel: 'sms' });

/**
 * The pre-send review. Reads the draft and answers whether to send it; it never
 * rewrites anything, which is the whole difference from `campaignCopy` above —
 * an operator on the Review step is holding a list of three thousand people and
 * asking one question, not asking for another version.
 *
 * One call for both channels, because the ANSWER's shape is the same and that
 * is what lets one panel render either. What it reads differs: a subject and
 * html, or the text and what it costs in segments.
 */
export const reviewCampaign = (body: {
  channel?: CampaignChannel;
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  bodySms?: string;
  audienceType?: CampaignAudience;
  fromName?: string;
}) => send<CampaignReview>('POST', '/ai/campaign_review', body);

// ---- Uploads ----------------------------------------------------------------

export const presignUpload = (fileName: string, contentType: string) =>
  send<{ s3Key: string; putUrl: string }>('POST', '/uploads/presign', {
    fileName,
    contentType,
  });

/** Raw S3 PUT — no auth headers, ~15 min validity on the url. */
export async function putToS3(putUrl: string, file: File): Promise<void> {
  const response = await fetch(putUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status}).`);
}

// ---- Campaigns --------------------------------------------------------------

export const listCampaigns = (page = 1, filters: CampaignListFilters = {}) => {
  const query = new URLSearchParams({ page: String(page) });
  if (filters.status) query.set('status', filters.status);
  if (filters.channel) query.set('channel', filters.channel);
  if (filters.q?.trim()) query.set('q', filters.q.trim());
  return getList<Campaign>(`/campaigns?${query}`);
};
/** The Campaigns home: this month against the same span of the last, filter counts, what needs attention. */
export const getCampaignOverview = () => getJson<CampaignHomeOverview>('/campaigns/overview');
/** Bookings that followed this month's sends and the same span of last month's — a dispatch read. */
export const getCampaignMonthBookings = () => getJson<CampaignMonthBookings>('/campaigns/overview/bookings');
/** The list's Booked column for a page of rows (at most 50 ids). */
export const getCampaignBookingCounts = (ids: number[]) =>
  getList<CampaignBookingCount>(`/campaigns/booking_counts?ids=${ids.join(',')}`);

export const getCampaign = (id: number) => getJson<Campaign>(`/campaigns/${id}`);

/**
 * The nullable fields are nullable on the wire, not merely optional: PATCH fills
 * anything the body omits from the stored row, so clearing a from-name or
 * dropping every audience filter has to say so with an explicit null.
 */
export interface CampaignDraft {
  name: string;
  /**
   * Sent on a CREATE only. The server refuses a change on an existing campaign
   * — the audience is addressed by a different column and the picks are a
   * different list, so a flipped channel would leave people picked by email
   * being texted — and a PATCH repeating it would 1001 every save.
   */
  channel?: CampaignChannel;
  inboxId: number;
  /** Null on a text campaign, which has neither. */
  subject: string | null;
  bodyHtml: string | null;
  bodyText?: string | null;
  /** The text message. Null on an email campaign. */
  bodySms?: string | null;
  fromName?: string | null;
  audienceType: CampaignAudience;
  customRecipients?: { email?: string; phoneNumber?: string; name?: string }[];
  audienceFilters?: CampaignAudienceFilters | null;
  throttlePerMinute?: number;
  /**
   * Whether to add the VISIBLE unsubscribe footer — the only part of opting out
   * that is the sender's choice. Honouring an opt-out already given is not, and
   * the `List-Unsubscribe` headers ride on every email campaign regardless.
   */
  includeUnsubscribe?: boolean;
  /** Quiet hours in tenant-clock minutes, both null for any time. */
  sendWindowStart?: number | null;
  sendWindowEnd?: number | null;
  /** Hours; null for no cap. Not carried onto a resend. */
  frequencyCapHours?: number | null;
  /** Email only. A new campaign defaults on server-side when this is absent. */
  trackClicks?: boolean;
  /** Leave out addresses that bounced on an earlier campaign. Off unless asked. */
  skipBounced?: boolean;
  /**
   * Contact people who have opted out, on THIS campaign only. Defaults false
   * everywhere, is gated at Campaigns: Send, and never edits the list itself —
   * the next campaign starts from the default again.
   *
   * It exists for the operational sends this platform is full of: a chauffeur
   * who replied STOP to a promotion still has to be told their shift moved. It
   * cannot make a CARRIER deliver — a STOP reply is enforced there and answers
   * 21610 whatever we send — so on SMS it reaches the rows the CRM was holding
   * back and the rest still fail.
   */
  contactOptedOut?: boolean;
  /**
   * The footer's own words, and the wording of its link. Empty or absent means
   * the default sentence, which names the company from the dispatch record —
   * so a tenant who never touches this is renamed when they rename themselves.
   * May carry merge tags, and a tag nothing fills refuses the send like one in
   * the body does.
   */
  unsubscribeFooterText?: string | null;
  unsubscribeLinkText?: string | null;
  /**
   * The name links to the tenant's own website carry as `utm_campaign`. Null or
   * blank tags nothing; the server tidies what it keeps (lower case, hyphens) and
   * stores none on a text campaign.
   */
  utmCampaign?: string | null;
}

export const createCampaign = (body: CampaignDraft) =>
  send<Campaign>('POST', '/campaigns', body);

/** Drafts only — the server 1001s an edit to a campaign that has already run. */
export const updateCampaign = (id: number, body: CampaignDraft) =>
  send<Campaign>('PATCH', `/campaigns/${id}`, body);

/**
 * The copy of a STOPPED campaign, and only the copy — same endpoint, and the
 * server refuses any other field by name rather than ignoring it. Everything else
 * has already been acted on by the half of the list that was mailed: the audience
 * is recipient rows, the mailbox and from-name are the identity those mails carry,
 * the throttle is the pace they went at.
 *
 * Needs `campaigns: full`, not `draft`: the mailed half went out on copy a
 * reviewer approved, so rewriting the other half is the sender's call.
 */
export const updateCampaignCopy = (
  id: number,
  body: { subject?: string; bodyHtml?: string; bodyText?: string | null; bodySms?: string },
) => send<Campaign>('PATCH', `/campaigns/${id}`, body);

/**
 * `inboxId` is optional but drives `sendingOptions` — quota belongs to the
 * sending mailbox, so without it the response cannot say whether the audience
 * fits or what spreading it would cost.
 */
export const previewAudience = (body: {
  audienceType: CampaignAudience;
  /**
   * Which column the audience is read out of, and which pair of individual
   * picks is honoured — email or phone.
   *
   * REQUIRED here although the server defaults it to email, because an omitted
   * channel is not an error anywhere: a text campaign asking without one was
   * answered with everyone who has an EMAIL and with its hand-picked
   * `selectedPhones` silently ignored, so a list of one came back as the whole
   * unfiltered pool. Every caller has the channel in hand; making them say it is
   * what stops the next one forgetting.
   */
  channel: CampaignChannel;
  customRecipients?: { email?: string; phoneNumber?: string; name?: string }[];
  audienceFilters?: CampaignAudienceFilters;
  inboxId?: number;
}) => send<AudiencePreview>('POST', '/campaigns/preview_audience', body);

/**
 * What a text body costs, priced by the SAME function the send path uses.
 *
 * The builder counts segments as the operator types (lib/smsSegments mirrors
 * the service), so this is not what makes the counter move — it is what keeps
 * the mirror honest, and it substitutes the tenant's REAL merge values, which
 * the composer cannot: `{{company_name}}` is eight characters in the box and
 * can be thirty on the wire.
 */
/**
 * The pre-flight review's measured half — the sender's health and record, and
 * what this list has done before — for the audience the builder describes,
 * resolved as the send will resolve it. The words are `reviewCampaign`'s.
 */
export const preflightCampaign = (body: {
  audienceType: CampaignAudience;
  channel: CampaignChannel;
  inboxId: number;
  customRecipients?: { email?: string; phoneNumber?: string; name?: string }[];
  audienceFilters?: CampaignAudienceFilters;
  frequencyCapHours?: number | null;
  skipBounced?: boolean;
  /** The draft being checked, so its own rows are not counted as history. */
  campaignId?: number;
}) => send<CampaignPreflight>('POST', '/campaigns/preflight', body);

export const previewSms = (body: { bodySms: string; sampleName?: string }) =>
  send<SmsPreview>('POST', '/campaigns/preview_sms', body);

/**
 * The merge tags this tenant can write into campaign copy. Tenant data points
 * are only listed when they resolve on the dispatch tenants row, so this is what
 * the tenant can actually send — not a catalogue.
 */
export const listMergeFields = () => getList<MergeField>('/campaigns/merge_fields');

/**
 * Which hosts are the tenant's own website — the only ones a campaign's links
 * are tagged for. The same resolver the send uses, so the builder's "3 links
 * will be tagged" is what the send does.
 */
export const getCampaignLinkTracking = () => getJson<CampaignLinkTracking>('/campaigns/link_tracking');

/**
 * What a campaign's tagged links brought to the website. Needs `analytics: view`
 * as well as the campaigns rung — a reader without it is refused, and Results
 * leaves the card out.
 */
export const getCampaignWebsite = (id: number) => getJson<CampaignWebsite>(`/campaigns/${id}/website`);

// ---- Short-link domain --------------------------------------------------------

/**
 * The company's footer wording. Read at campaigns `view` — the builder shows it
 * as the placeholder of its own footer fields — and written at `full`. Empty
 * strings clear it back to the built-in wording; a data point nothing fills is
 * refused on save with the sentence the send would have used.
 */
export const getCampaignSettings = () => getJson<CampaignSettings>('/campaigns/settings');
export const saveCampaignSettings = (body: { footer: { text: string; linkText: string } }) =>
  send<CampaignSettings>('PUT', '/campaigns/settings', body);

export const getShortLinkDomain = () => getJson<ShortLinkDomainState>('/short_link_domain');
/** Saves the host and checks its DNS at once; links use it only once verified. */
export const saveShortLinkDomain = (host: string) =>
  send<ShortLinkDomainState>('PUT', '/short_link_domain', { host });
export const verifyShortLinkDomain = () =>
  send<ShortLinkDomainState>('POST', '/short_link_domain/verify', {});
export const removeShortLinkDomain = () => send<ShortLinkDomainState>('DELETE', '/short_link_domain');

// ---- Automated campaigns ----------------------------------------------------

export type AutomationDraft = Partial<
  Pick<
    CampaignAutomation,
    | 'name'
    | 'trigger'
    | 'delayHours'
    | 'lapsedDays'
    | 'audienceType'
    | 'channel'
    | 'inboxId'
    | 'subject'
    | 'bodyHtml'
    | 'bodyText'
    | 'bodySms'
    | 'fromName'
    | 'includeUnsubscribe'
    | 'sendWindowStart'
    | 'sendWindowEnd'
    | 'frequencyCapHours'
    | 'trackClicks'
  >
>;

export const listAutomations = () => getList<CampaignAutomation>('/campaign_automations');
/**
 * "Booked again" per automation — trips made within 7 days of a send from any of
 * its runs, once each. A dispatch read of its own, asked after the list.
 */
export const listAutomationBookings = () => getList<CampaignBookingCount>('/campaign_automations/booked');
export const getAutomation = (id: number) => getJson<CampaignAutomation>(`/campaign_automations/${id}`);
export const createAutomation = (body: AutomationDraft) =>
  send<CampaignAutomation>('POST', '/campaign_automations', body);
/** Draft or paused only — the server refuses an edit to one that is on. */
export const updateAutomation = (id: number, body: AutomationDraft) =>
  send<CampaignAutomation>('PATCH', `/campaign_automations/${id}`, body);
export const deleteAutomation = (id: number) =>
  send<{ deleted: true }>('DELETE', `/campaign_automations/${id}`);
/** Campaigns: Send — every check a send gets, asked now. */
export const activateAutomation = (id: number) =>
  send<CampaignAutomation>('POST', `/campaign_automations/${id}/activate`, {});
export const pauseAutomation = (id: number) =>
  send<CampaignAutomation>('POST', `/campaign_automations/${id}/pause`, {});
export const previewAutomation = (
  body: Pick<AutomationDraft, 'trigger' | 'delayHours' | 'lapsedDays' | 'audienceType' | 'channel'>,
) => send<AutomationPreview>('POST', '/campaign_automations/preview', body);

/** Trips the recipients booked within `days` (default 7, at most 90) of their send. */
export const getCampaignBookings = (id: number, days = 7) =>
  getJson<CampaignBookings>(`/campaigns/${id}/bookings?days=${days}`);

/** A finished campaign's AI summary (null until written) and its next steps. */
export const getCampaignSummary = (id: number) =>
  getJson<CampaignSummaryPayload>(`/campaigns/${id}/summary`);

/**
 * Write the summary. One written in the last minute comes back as it stands
 * unless `force` — "Write it again" passes it.
 */
export const writeCampaignSummary = (id: number, force = false) =>
  send<CampaignSummaryPayload>('POST', `/campaigns/${id}/summary`, force ? { force: true } : {});

/** Opens (or receipts) and clicks per hour since the start, `hours` long (at most 72). */
export const getCampaignActivity = (id: number, hours = 24) =>
  getJson<CampaignActivity>(`/campaigns/${id}/activity?hours=${hours}`);

/** Which targeting filters each dispatch audience supports (live-schema derived). */
export const getAudienceMeta = (channel: CampaignChannel = 'email') =>
  getJson<{ meta: AudienceMeta }>(`/campaigns/audience_meta?channel=${channel}`);

/**
 * The builder's recipient picker: paginated + searchable, 50 per page.
 *
 * `scope` picks which list is being asked for, and they answer different
 * questions: 'pool' (the default) is everyone the filters match — what picks are
 * made FROM — while 'selected' is who is actually on the campaign, the pool
 * narrowed by the individual picks. Send the FULL filters with 'selected'
 * (`toApiFilters`); the pool listing wants only `toPoolFilters`.
 */
export const listAudienceMembers = (body: {
  audienceType: DispatchAudience;
  /** Required for `previewAudience`'s reason — see the note there. */
  channel: CampaignChannel;
  audienceFilters?: CampaignAudienceFilters;
  scope?: 'pool' | 'selected';
  q?: string;
  page?: number;
}) => send<AudienceMembersResult>('POST', '/campaigns/audience_members', body);

/**
 * The do-not-text list. Reading it answers "why didn't they get it"; adding to
 * it is the protective direction and sits one rung below sending. Taking one
 * off is guarded by its REASON rather than a rung — a customer's own STOP is
 * not an operator's to reverse, and the server refuses it with that sentence.
 */
export const listSuppressions = (params: {
  q?: string;
  page?: number;
  channel?: 'email' | 'sms';
} = {}) => {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.page) query.set('page', String(params.page));
  if (params.channel) query.set('channel', params.channel);
  const suffix = query.toString();
  return getJson<{
    payload: Suppression[];
    total: number;
    page: number;
    pageSize: number;
  }>(`/suppressions${suffix ? `?${suffix}` : ''}`);
};

export const addSuppression = (body: {
  channel: 'email' | 'sms';
  address: string;
  note?: string;
}) => send<Suppression>('POST', '/suppressions', body);

export const removeSuppression = (id: number) =>
  send<{ deleted: true }>('DELETE', `/suppressions/${id}`);

/**
 * `sendMode` picks the transport. 'ses' 1001s when the credit balance is short
 * or the deployment has no SES configured; the server's message names the
 * shortfall, so surface it rather than rewording it.
 *
 * On a **scheduled** campaign this is Send now, and `sendMode` is IGNORED — the
 * transport was chosen when the time was set, and letting a press move a
 * credit-funded blast onto the tenant's own mailbox would split one campaign
 * across two From identities.
 */
/**
 * Hand a submission back to its author as a draft. Needs `campaigns: full`,
 * like sending it; the two are the reviewer's only answers to `in_review`.
 */
export const returnCampaign = (id: number, note?: string) =>
  send<{ status: 'draft' }>('POST', `/campaigns/${id}/return`, note ? { note } : {});

export const sendCampaign = (id: number, sendMode: CampaignSendMode = 'spread') =>
  send<{ recipientCount: number; sendMode: CampaignSendMode }>(
    'POST',
    `/campaigns/${id}/send`,
    { sendMode },
  );

/**
 * Mail a finished campaign again — and note what comes back is a **draft**, not a
 * send. The server writes a new campaign carrying this one's copy, mailbox and a
 * frozen list of the chosen recipients; putting it in front of anyone is the
 * ordinary `sendCampaign` afterwards, which is what keeps the quota check, the
 * credit reservation and the review step on one path.
 *
 * `segment: 'custom'` needs `emails`, and every one must already be a recipient
 * of this campaign — a stranger in the list is a 1001 for the whole call rather
 * than a resend to the rest.
 *
 * `skippedRecipients` describes THIS call, not the row: addresses the campaign
 * really reached that a draft cannot hold, because dispatch admits any address
 * with an `@` in it and a campaign's own recipients are validated on the way
 * back in. Non-zero means fewer people than the count you offered.
 */
export const resendCampaign = (
  id: number,
  /** `phones` on a text campaign, `emails` on an email one — never both. */
  body: { segment: CampaignResendSegment; emails?: string[]; phones?: string[] },
) => send<Campaign & { skippedRecipients: number }>('POST', `/campaigns/${id}/resend`, body);

/**
 * Stop a campaign that is going out. Needs `campaigns: full` — the level that
 * started it.
 *
 * Nothing is cancelled in the queue: the status is what both the running tick and
 * its chain read, so the tick stops mid-window and does not re-arm. `pending` is
 * who is left, and `creditsRefunded` is what came back off a credit-funded send —
 * a stop settles the reservation rather than holding it for a resume that may
 * never come, which is the whole point on a campaign fired by mistake.
 *
 * 1003 when the campaign finished in the moment before the press landed.
 */
export const stopCampaign = (id: number) =>
  send<{ status: 'stopped'; pending: number; creditsRefunded: number }>(
    'POST',
    `/campaigns/${id}/stop`,
    {},
  );

/**
 * Start a stopped campaign again, from where it left off — the same campaign row,
 * so the funnel keeps counting one run.
 *
 * The transport is not re-asked: a credit-funded campaign reserves again for the
 * recipients still pending, and 1001s with the shortfall if the balance no longer
 * covers them. It also re-checks the mailbox grant and the copy, because a
 * campaign can sit stopped for days. `status` comes back `completed` rather than
 * `sending` when the stop landed on the last window and there was nobody left.
 */
export const resumeCampaign = (id: number) =>
  send<{ status: 'sending' | 'completed'; pending: number }>(
    'POST',
    `/campaigns/${id}/resume`,
    {},
  );

/**
 * Send it later. `campaigns: full`, the level that would have sent it now — a
 * schedule IS the send decision, with only the carrying-out deferred.
 *
 * `scheduledAt` is a UTC instant at least a minute out and at most a year, and
 * turning the operator's wall-clock choice into one is the caller's job (see
 * `instantFromWall` — the tenant's zone, never the browser's).
 *
 * **Nothing is resolved or reserved until it fires.** The audience is read at the
 * moment it goes, so a customer who signs up on Thursday is on Friday's blast —
 * and a `ses` schedule can genuinely reach 03:00 with the credits gone, which
 * lands as `status: "failed"` with the shortfall in `error` rather than as mail
 * quietly sent some other way. Say that where the choice is made.
 *
 * Re-scheduling an already-scheduled campaign is how it moves; `unschedule` is
 * how it stops.
 */
export const scheduleCampaign = (
  id: number,
  body: { scheduledAt: string; sendMode?: CampaignSendMode },
) => send<Campaign>('POST', `/campaigns/${id}/schedule`, body);

/**
 * Call it off — back to `draft`, which is what a campaign holding no recipients
 * is. 1003 when the sweeper fired it in the same second; stop it instead.
 */
export const unscheduleCampaign = (id: number) =>
  send<{ status: 'draft' }>('POST', `/campaigns/${id}/unschedule`, {});

/**
 * Mail (or text) the campaign as it stands to the person pressing the button —
 * their own dispatch address or phone unless `to` names another. Marked
 * `[Test]`, spends no credits, writes no recipient row.
 */
export const sendCampaignTest = (id: number, to?: string) =>
  send<{ sent: true; to: string }>('POST', `/campaigns/${id}/test`, to ? { to } : {});

/** The recipient table as CSV, inside the envelope; the caller makes the download. */
export const exportCampaignRecipients = (id: number) =>
  getJson<{ filename: string; csv: string; rows: number }>(`/campaigns/${id}/export`);

export const listCampaignRecipients = (
  id: number,
  options: { status?: CampaignRecipientFilter; page?: number } = {},
) => {
  const params = new URLSearchParams({ page: String(options.page ?? 1) });
  if (options.status) params.set('status', options.status);
  return getList<CampaignRecipient>(`/campaigns/${id}/recipients?${params}`);
};

// ---- Calls ------------------------------------------------------------------

/**
 * The call-log endpoint does not exist on the backend yet — CallsView calls
 * this optimistically and falls back to its empty state on any error, so the
 * tab lights up the moment the API ships.
 */
export const listCalls = () => getJson<CallLogResult>('/calls');

// ---- Notifications ----------------------------------------------------------

export const listNotifications = (options: { unread?: boolean; page?: number } = {}) => {
  const params = new URLSearchParams({ page: String(options.page ?? 1) });
  if (options.unread) params.set('unread', 'true');
  return getJson<NotificationListResult>(`/notifications?${params}`);
};

export const notificationUnreadCount = () =>
  getJson<{ count: number }>('/notifications/unread_count');

export const markNotificationRead = (id: number) =>
  send<{ id: number }>('POST', `/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  send<{ count: number }>('POST', '/notifications/read_all');

export const getNotificationSettings = () =>
  getJson<{ preferences: NotificationPreferences }>('/notifications/settings');

/** Partial updates merge server-side; unknown keys and non-booleans are ignored. */
export const putNotificationSettings = (preferences: Partial<NotificationPreferences>) =>
  send<{ preferences: NotificationPreferences }>('PUT', '/notifications/settings', {
    preferences,
  });

export const listNotificationMutes = () => getList<NotificationMute>('/notifications/mutes');

/**
 * Re-targeting the same thing updates it rather than erroring — including
 * flipping its mode, so "Notify me" on a channel you had muted becomes a watch.
 */
export const createNotificationMute = (body: {
  scope: 'inbox' | 'conversation';
  /** Defaults to 'mute'. */
  mode?: 'mute' | 'watch';
  inboxId?: number;
  conversationDisplayId?: number;
  /** null/omitted = permanent; otherwise a FUTURE ISO timestamp. */
  until?: string | null;
}) => send<NotificationMute>('POST', '/notifications/mutes', body);

export const deleteNotificationMute = (id: number) =>
  send<{ id: number }>('DELETE', `/notifications/mutes/${id}`);

/**
 * Per-channel overrides of the type toggles, keyed by inbox id. Only the keys an
 * agent actually changed are present; everything else falls through to their
 * global settings and then to the defaults.
 */
export const getNotificationInboxSettings = () =>
  getJson<{ payload: Record<string, Partial<NotificationPreferences>> }>(
    '/notifications/inbox_settings',
  ).then((data) => data.payload);

export const putNotificationInboxSettings = (
  inboxId: number,
  preferences: Partial<NotificationPreferences>,
) =>
  send<{ inboxId: number; preferences: Partial<NotificationPreferences> }>(
    'PUT',
    `/notifications/inbox_settings/${inboxId}`,
    { preferences },
  );

/** Drop the override — the channel returns to the agent's global settings. */
export const clearNotificationInboxSettings = (inboxId: number) =>
  send<{ inboxId: number; cleared: boolean }>(
    'DELETE',
    `/notifications/inbox_settings/${inboxId}`,
    {},
  );

/**
 * Register this browser for background push. The token identifies a browser
 * profile; re-registering moves it to the current agent, so a shared machine
 * stops pushing the previous person's conversations.
 */
export const registerPushDevice = (token: string, platform = 'web') =>
  send<{ id: number; registered: boolean }>('POST', '/notifications/devices', { token, platform });

export const unregisterPushDevice = (token: string) =>
  send<{ unregistered: boolean }>('DELETE', `/notifications/devices/${encodeURIComponent(token)}`);

// ---- Contact ownership (crm_config) ----------------------------------------

/**
 * Who owns this customer. Ownership drives auto-assignment on every channel and,
 * for agents restricted to "Only my customers", what they can see at all.
 *
 * `crm_config` only — a 1036 here means the caller is not a channel admin, which
 * the contact panel treats as "don't show the card" rather than an error.
 */
export const listContactOwners = (contactId: number) =>
  getList<{ dashBoardId: number }>(`/access/contacts/${contactId}/owners`);

export const setContactOwners = (contactId: number, dashBoardIds: number[]) =>
  send<{ contactId: number; dashBoardIds: number[] }>(
    'POST',
    `/access/contacts/${contactId}/owners`,
    { dashBoardIds },
  );

// ---- Website analytics ------------------------------------------------------

/**
 * Behind the seam like everything else the module renders, so `/comms-preview`
 * is forced to stub them rather than reaching for a session. The standalone
 * `/analytics` route imports `api/analytics` directly; only the embedded and
 * preview mounts come through here.
 */
export {
  createAnalyticsAnnotation,
  createAnalyticsSite,
  deleteAnalyticsAnnotation,
  deleteAnalyticsSite,
  getAnalyticsDashboard,
  getAnalyticsFunnel,
  getAnalyticsGeo,
  getAnalyticsHeatmap,
  getAnalyticsLive,
  getAnalyticsOverview,
  getAnalyticsSeries,
  getAnalyticsSession,
  getAnalyticsTop,
  getAnalyticsTrafficTable,
  refreshAnalyticsGeo,
  saveAnalyticsGeoKey,
  listAnalyticsAnnotations,
  listAnalyticsSessions,
  listAnalyticsSites,
  purgeAnalyticsVisitor,
  saveAnalyticsDashboard,
  updateAnalyticsSite,
} from './analytics';
// The analytics screen asks which install the booking-widget pages report to.
export { getSiteIntegrations } from './siteIntegrations';
